import { KeyLayout } from "./piano-layout";

/**
 * Min/max white-key width in CSS px, **au pointeur fin** (souris, trackpad).
 *
 * - Below MIN, the keyboard would be unreadable, so instead of shrinking
 *   further the content overflows the canvas and becomes horizontally
 *   scrollable (pan pitch).
 * - Above MAX, keys would look oversized on wide screens, so the content is
 *   capped and centred with margins instead of being stretched.
 *
 * MAX est le plafond des grands écrans ; sur un canvas étroit il descend, voir
 * {@link maxWhiteKeyWidthFor}.
 *
 * MIN à 20 px est un plancher de **lisibilité**, pas de jouabilité : à la
 * souris on vise un pixel, 20 px suffisent à cliquer et à lire la touche. Ne
 * jamais le relire comme une taille de cible tactile — 20 px valent 3,8 mm sur
 * un iPad, on n'y pose pas un doigt. Au doigt, c'est
 * {@link MIN_WHITE_KEY_WIDTH_COARSE} qui fait foi.
 */
export const MIN_WHITE_KEY_WIDTH = 20;
export const MAX_WHITE_KEY_WIDTH = 32;

/**
 * Bornes de largeur de touche blanche au **pointeur grossier** (doigt, stylet).
 *
 * Conversion utile pour relire ces nombres : un iPad affiche 264 ppi en 2×,
 * soit 132 px CSS par pouce → **1 px CSS = 0,19 mm** ; un iPhone 15 affiche
 * 460 ppi en 3× → 0,17 mm. Une vraie touche blanche de piano fait 23 mm.
 *
 * Les 32 px du plafond « pointeur fin » ne font donc que 6,2 mm sur iPad, un
 * quart d'une vraie touche et bien en dessous des minimums de cible tactile
 * (44 pt chez Apple, 48 dp chez Material). Le plancher monte donc au doigt,
 * sans faire semblant d'approcher les 23 mm réels (il faudrait 120 px, donc
 * 8 touches à l'écran, le morceau ne serait plus lisible du tout).
 *
 * Ce plancher change la nature du clavier au doigt : il cesse d'être une vue
 * « qui cadre le morceau » pour devenir un instrument à taille de touche
 * quasi fixe, qu'on rejoint en faisant défiler — comme on déplace les mains
 * sur un vrai piano. Le plafond ne sert plus qu'à empêcher un exercice à cinq
 * doigts de gonfler jusqu'à l'absurde.
 *
 * ## Pourquoi 40 et non 48
 *
 * 48 px tenait la cible Material au pied de la lettre (9,2 mm sur iPad) et
 * ne laissait voir que **20 touches blanches**, soit à peine trois octaves,
 * sur les ~964 px de canvas d'un iPad 1024. C'est le reproche qui est
 * remonté du terrain, et la mesure lui donne raison : sur les 104 MIDI du
 * corpus (204 000 échantillons de 100 ms), 20 touches contiennent
 * l'intégralité de l'accord en train de sonner **87 %** du temps et la
 * fenêtre « ce qui arrive dans les 1,5 s » **66 %** du temps ; à 26 touches
 * on passe à **97 %** et **89 %**. Le saut est là, entre 20 et 26.
 *
 * 40 px donne 24 touches sur le canvas le plus étroit de la famille et 27 à
 * 29 sur un iPad Air ou Pro — donc les ~26 touches que la règle « pointeur
 * fin » vise déjà de son côté, par un autre chemin. Les deux règles cessent
 * de se contredire au lieu de s'arbitrer.
 *
 * Ce que ça coûte : 7,6 mm au lieu de 9,2 sur iPad, sous la cible Material,
 * au-dessus des 44 pt d'Apple à un poil près. La cible n'a jamais été tenue
 * ici de toute façon : une touche **noire** vaut 0,58 fois une blanche, donc
 * 28 px déjà à l'époque de 48, et 23 px maintenant. Un plancher qui protège
 * la blanche au nom d'un minimum que la noire viole d'un tiers ne protège
 * rien ; ce qui rend une touche jouable au doigt sur cet écran, c'est la
 * hauteur de bande (117 px, 22 mm) et le fait qu'il n'y a rien d'autre à
 * côté à ne pas toucher.
 */
export const MIN_WHITE_KEY_WIDTH_COARSE = 40;
export const MAX_WHITE_KEY_WIDTH_COARSE = 56;

/**
 * Plafond de largeur de touche sur canvas étroit, et tessiture qu'il sert.
 *
 * Le plafond ne borne QUE les morceaux étroits : dès que la tessiture dépasse
 * `TARGET_VISIBLE_WHITE_KEYS`, la largeur vient déjà de `width / baseWhite` et
 * passe sous le plafond toute seule. Pour les autres, garder 32 px sur un
 * canvas de ~620 px (iPhone SE en paysage, minimap déduite) ne laisse voir que
 * 19 touches blanches, soit moins de trois octaves : on gonfle un petit
 * morceau au lieu de montrer le clavier autour. Le plafond descend donc
 * jusqu'à `MAX_WHITE_KEY_WIDTH_COMPACT` pour garder ~26 touches à l'écran,
 * de quoi jouer à deux mains — et rend au passage ~30 px de hauteur au roll,
 * la bande de touches dérivant de cette même largeur.
 *
 * Le plafond plein est atteint dès `26 × 32 = 832 px` disponibles, très en
 * dessous des ~1020 px du plus étroit des écrans ≥ 1280 px (sidebar ouverte
 * + minimap) : rien ne bouge à partir de 1280 px.
 *
 * Entre 1024 et ~1150 px **sidebar ouverte**, en revanche, la largeur
 * disponible tombe sous 832 px et le plafond mord un peu (~29-30 px au lieu de
 * 32), ce qui raccourcit la bande de touches de quelques pixels. C'est assumé :
 * le plafond raisonne sur la place réellement offerte au clavier, pas sur la
 * taille de la fenêtre, et sur un canvas aussi étroit montrer plus de touches
 * vaut mieux que les garder larges. Ne pas lire cette garantie comme un
 * « aucun changement au-dessus de 1024 px », elle ne l'est pas.
 */
const MAX_WHITE_KEY_WIDTH_COMPACT = 24;
const TARGET_VISIBLE_WHITE_KEYS = 26;

/**
 * Plafond de largeur de touche pour une largeur disponible donnée (largeur de
 * canvas moins la minimap), **au pointeur fin**. Voir
 * {@link MAX_WHITE_KEY_WIDTH_COMPACT}.
 */
function maxWhiteKeyWidthFor(availableWidth: number): number {
  return Math.max(
    MAX_WHITE_KEY_WIDTH_COMPACT,
    Math.min(MAX_WHITE_KEY_WIDTH, availableWidth / TARGET_VISIBLE_WHITE_KEYS),
  );
}

/** Bornes de largeur de touche blanche, en px CSS. */
export interface WhiteKeyWidthBounds {
  readonly min: number;
  readonly max: number;
}

/**
 * Bornes applicables pour une largeur disponible et un type de pointeur.
 *
 * Le type de pointeur est **passé en paramètre**, pas lu ici : ce module est
 * l'autorité de géométrie et doit rester pur — il est importé par du code rendu
 * côté serveur (`DEFAULT_KEYBOARD_HEIGHT` amorce l'observable du Studio). C'est
 * le runtime Pixi, seul client du navigateur, qui interroge le média.
 *
 * Au doigt, la garantie « ~26 touches blanches à l'écran » de
 * {@link MAX_WHITE_KEY_WIDTH_COMPACT} ne s'applique pas telle quelle : c'est
 * une règle de **largeur de canvas** (montrer assez de clavier sur un écran
 * étroit) et non de cible tactile. Le plancher tactile de 40 px y arrive
 * pourtant de lui-même sur la famille iPad, à une ou deux touches près, sans
 * qu'aucune des deux règles ait à céder — voir
 * {@link MIN_WHITE_KEY_WIDTH_COARSE}.
 */
export function whiteKeyWidthBoundsFor(
  availableWidth: number,
  coarsePointer: boolean,
): WhiteKeyWidthBounds {
  if (coarsePointer) {
    return { min: MIN_WHITE_KEY_WIDTH_COARSE, max: MAX_WHITE_KEY_WIDTH_COARSE };
  }

  return { min: MIN_WHITE_KEY_WIDTH, max: maxWhiteKeyWidthFor(availableWidth) };
}

/** Black-key width as a fraction of the white-key width. */
export const BLACK_KEY_RATIO = 0.58;

/**
 * Keyboard strip height per unit of white-key width. Picked to preserve the
 * historical aspect around a ~30px white key (110 / 30 ≈ 3.667): the strip
 * height is derived from the *clamped* white-key width so keys keep a constant
 * proportion as the width is clamped within [MIN, MAX].
 *
 * Resulting strip heights: ~73px at MIN (20px), ~88px at the compact cap
 * (24px) and ~117px at MAX (32px).
 *
 * **Ce ratio ne s'applique jamais aux largeurs tactiles.** La bande serait
 * sinon deux fois plus haute au doigt (147 px à 40 px de touche, 205 px à
 * 56 px) et mangerait le piano-roll, qui est la surface de lecture. La hauteur
 * reste pilotée par les bornes « pointeur fin » quel que soit le pointeur, voir
 * `PianoComponent.keyboardHeightForWidth`.
 */
export const KEYBOARD_HEIGHT_PER_WHITE_KEY = 110 / 30;

/**
 * Default keyboard strip height, at MAX white-key width. Seeds the live
 * keyboard-height observable and serves as the overlay fallback before the
 * first Pixi layout publishes a measured height.
 */
export const DEFAULT_KEYBOARD_HEIGHT = Math.round(
  MAX_WHITE_KEY_WIDTH * KEYBOARD_HEIGHT_PER_WHITE_KEY,
);

/**
 * Single authority for the horizontal (pitch -> screen-x) projection shared by
 * the keyboard and every piano-roll sublayer (notes, octave grid, hints,
 * presses, hit-testing).
 *
 * The grid is snapped in *content space* (`round(whiteIndex * whiteKeyWidth)`)
 * so two adjacent white keys always share the exact same pixel boundary, with
 * no gaps and no overlaps; a uniform `offsetX` is then added *after* the snap.
 * Adding the offset after rounding keeps key adjacency pixel-perfect for any
 * offset value, including the fractional/animated offsets produced by the
 * auto-follow ease.
 *
 * `offsetX` carries two distinct meanings depending on the layout:
 * - the large-screen centring margin (>= 0) when the content fits the canvas;
 * - the horizontal scroll/pan (<= 0) when the content is wider than the canvas.
 */
export class HorizontalProjection {
  constructor(
    readonly whiteKeyWidth: number,
    readonly blackKeyWidth: number,
    readonly offsetX: number,
  ) {}

  /** Left edge x of a white key given its white index. */
  whiteLeft(whiteIndex: number): number {
    return Math.round(whiteIndex * this.whiteKeyWidth) + this.offsetX;
  }

  /** Right edge x of a white key given its white index. */
  whiteRight(whiteIndex: number): number {
    return Math.round((whiteIndex + 1) * this.whiteKeyWidth) + this.offsetX;
  }

  /** Boundary between two adjacent white keys; black keys are centred on it. */
  boundary(whiteIndex: number): number {
    return Math.round((whiteIndex + 1) * this.whiteKeyWidth) + this.offsetX;
  }

  /** Left edge x of a black key given its (attached) white index. */
  blackLeft(whiteIndex: number): number {
    return this.boundary(whiteIndex) - Math.floor(this.blackKeyWidth / 2);
  }

  /** Left edge of any key (white or black). */
  keyLeft(key: KeyLayout): number {
    if (key.color === "white") {
      return this.whiteLeft(key.whiteIndex);
    }

    return this.blackLeft(key.whiteIndex);
  }

  /** On-screen width of any key (white or black). */
  keyWidth(key: KeyLayout): number {
    if (key.color === "white") {
      return this.whiteRight(key.whiteIndex) - this.whiteLeft(key.whiteIndex);
    }

    return this.blackKeyWidth;
  }

  /** Horizontal centre of any key (white or black). */
  keyCenter(key: KeyLayout): number {
    if (key.color === "white") {
      return (this.whiteLeft(key.whiteIndex) + this.whiteRight(key.whiteIndex)) / 2;
    }

    return this.boundary(key.whiteIndex);
  }
}
